<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'
import { onClickOutside, useScroll } from '@vueuse/core'
import { VButton, VIcon } from '@/modules/shared/components'
import OnboardingProgressBar from '@/modules/connecting/components/onboarding-progress-bar.vue'
import type { OnboardingStep } from '@/modules/shared/composables/use-onboarding'

const props = defineProps<{
  steps: OnboardingStep[]
  currentStep: number
  highestStepReached: number
  isFirstStep: boolean
  isLastStep: boolean
  stepMenuOpen: boolean
  loading?: boolean
  submitting?: boolean
  submitLabel?: string
  submitVariant?: string
  continueLabel?: string
  showLogo?: boolean
}>()

const emit = defineEmits<{
  (e: 'continue'): void
  (e: 'back'): void
  (e: 'submit'): void
  (e: 'step-click', idx: number): void
  (e: 'update:stepMenuOpen', value: boolean): void
}>()

const currentMeta = computed(() => props.steps[props.currentStep])
const isStepReachable = (idx: number) => idx <= props.highestStepReached

const stepNavRef = ref<HTMLElement | null>(null)
const scrollContainer = ref<HTMLElement | null>(null)

onClickOutside(stepNavRef, () => {
  emit('update:stepMenuOpen', false)
})

const { arrivedState } = useScroll(scrollContainer)
const canScrollDown = computed(() => !arrivedState.bottom)

const scrollToBottom = () => {
  scrollContainer.value?.scrollTo({ top: scrollContainer.value.scrollHeight, behavior: 'smooth' })
}

watch(
  () => props.currentStep,
  () => nextTick(() => scrollContainer.value?.dispatchEvent(new Event('scroll'))),
)

const toggleStepMenu = () => {
  emit('update:stepMenuOpen', !props.stepMenuOpen)
}

const onStepMenuClick = (idx: number) => {
  emit('step-click', idx)
  emit('update:stepMenuOpen', false)
}
</script>

<template>
  <div class="relative flex max-h-screen min-h-screen flex-col overflow-hidden bg-gray-50">
    <slot name="notification" />

    <!-- Top: progress + logo (fixed, transparent) -->
    <div class="ob-topbar pointer-events-none fixed inset-x-0 top-0 z-50">
      <OnboardingProgressBar :current="currentStep" :total="steps.length" />
      <div class="ob-header flex items-center justify-between px-6 pt-5">
        <img v-if="showLogo !== false" src="/venture360-logo.svg" class="h-6 w-auto" alt="Venture360" />
        <div v-else />
        <div class="relative" ref="stepNavRef">
          <button
            class="flex cursor-pointer items-center rounded-full border-none bg-white py-[5px] pl-[14px] pr-[10px] font-[inherit] text-[13px] leading-none shadow-sm ring-1 ring-gray-700/20 transition-all duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] [font-variant-numeric:tabular-nums] hover:shadow-md hover:ring-gray-700/30 active:translate-y-px active:shadow-sm"
            @click="toggleStepMenu"
          >
            <span class="font-bold text-[#009192]">{{ currentStep + 1 }}</span>
            <span class="mx-1 font-normal text-gray-300">/</span>
            <span class="font-medium text-gray-400">{{ steps.length }}</span>
            <svg
              class="ml-1 text-gray-400 transition-transform duration-150 ease-linear"
              :class="{ 'rotate-180': stepMenuOpen }"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
              stroke-linecap="round"
              stroke-linejoin="round"
              width="12"
              height="12"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <div
            v-if="stepMenuOpen"
            class="step-menu z-60 absolute right-0 top-[calc(100%+8px)] min-w-[260px] origin-top rounded-xl bg-white p-1.5 shadow-[0_4px_24px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.06)]"
          >
            <button
              v-for="(step, idx) in steps"
              :key="step.key"
              class="flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-none bg-transparent px-3 py-2 text-left font-[inherit] text-[13px] font-medium text-gray-700 transition-colors duration-100 enabled:hover:bg-gray-100 enabled:active:bg-gray-200"
              :class="{
                'bg-[rgba(0,145,146,0.06)]': idx === currentStep,
                'cursor-not-allowed opacity-40': !isStepReachable(idx),
              }"
              :disabled="!isStepReachable(idx)"
              @click="onStepMenuClick(idx)"
            >
              <span
                class="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
                :class="idx === currentStep ? 'bg-[#009192] text-white' : 'bg-gray-100 text-gray-500'"
                >{{ idx + 1 }}</span
              >
              <span
                :class="
                  idx === currentStep ? 'font-semibold text-[#009192]' : !isStepReachable(idx) ? 'text-gray-400' : ''
                "
                >{{ step.title }}</span
              >
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Scrollable content -->
    <div
      ref="scrollContainer"
      class="onboarding-scroll top-section flex flex-1 justify-center overflow-y-auto [scrollbar-width:none]"
    >
      <div class="ob-content flex w-full max-w-[600px] flex-col px-5 pt-[72px] max-sm:px-4 max-sm:pt-8">
        <!-- Loading -->
        <div
          v-if="loading"
          class="flex flex-1 animate-[fadeIn_0.4s_ease_0.3s_forwards] items-center justify-center py-20 opacity-0"
        >
          <slot name="loading">
            <div class="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#009192]" />
          </slot>
        </div>

        <div v-else class="flex flex-1 flex-col">
          <!-- Step content (keyed for entrance animation) -->
          <div :key="currentStep" class="step-body flex flex-1 flex-col">
            <!-- Step icon -->
            <div
              v-if="currentMeta?.icon"
              class="step-icon mb-6 flex h-14 w-14 items-center justify-center rounded-2xl border border-[rgba(0,145,146,0.12)] bg-[linear-gradient(135deg,rgba(0,145,146,0.08),rgba(0,132,180,0.08))] text-[#009192]"
            >
              <VIcon :name="currentMeta.icon" class="h-6 w-6" />
            </div>

            <h2 class="mb-2 text-[26px] font-bold leading-[1.2] tracking-tight text-gray-900 max-sm:text-[22px]">
              {{ currentMeta.title }}
            </h2>
            <p v-if="currentMeta.subtitle" class="mb-9 text-[15px] leading-[1.6] text-gray-500 [text-wrap:pretty]">
              {{ currentMeta.subtitle }}
            </p>

            <div class="flex-1">
              <slot />
            </div>
          </div>
        </div>
        <div class="h-[90px] shrink-0" />
      </div>
    </div>

    <!-- Scroll-to-bottom indicator -->
    <Transition
      enter-active-class="transition duration-200 ease-[cubic-bezier(0.22,1,0.36,1)]"
      enter-from-class="translate-y-4 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-[cubic-bezier(0.22,1,0.36,1)]"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-4 opacity-0"
    >
      <button
        v-if="canScrollDown && !loading"
        class="scroll-hint fixed bottom-[72px] left-1/2 z-40 flex h-9 w-9 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full border-none bg-white/80 shadow-sm ring-1 ring-gray-700/20 backdrop-blur-[16px] transition-shadow duration-150 [backdrop-filter:blur(16px)_saturate(1.5)] hover:shadow-md hover:ring-gray-700/30 active:translate-y-px active:shadow-sm"
        @click="scrollToBottom"
      >
        <svg
          class="scroll-hint-arrow text-gray-400"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M12 5v14m-6-6l6 6 6-6" />
        </svg>
      </button>
    </Transition>

    <!-- Fixed bottom nav bar -->
    <div
      v-if="!loading"
      class="ob-bottombar fixed inset-x-0 bottom-0 z-40 border-t border-[rgba(0,0,0,0.06)] bg-white/80 backdrop-blur-[16px] [backdrop-filter:blur(16px)_saturate(1.5)]"
    >
      <div class="mx-auto flex max-w-[600px] items-center gap-2.5 px-5 py-2.5">
        <VButton v-if="!isFirstStep" variant="secondary" size="lg" :click="() => $emit('back')">&#8592; Back</VButton>
        <div v-else />
        <VButton
          v-if="isLastStep"
          class="flex-1 text-center"
          :variant="submitVariant ?? 'v-green'"
          size="lg"
          :loading="submitting"
          :click="() => $emit('submit')"
          >{{ submitLabel ?? 'Done' }}</VButton
        >
        <VButton v-else class="flex-1 text-center" variant="v-blue" size="lg" :click="() => $emit('continue')"
          >{{ continueLabel ?? 'Continue' }} &#8594;</VButton
        >
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Pointer-events passthrough for topbar children */
.ob-header > * {
  pointer-events: auto;
}

/* Step menu dropdown animation */
.step-menu {
  animation: menuIn 0.15s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes menuIn {
  from {
    opacity: 0;
    transform: perspective(1000px) translateY(-8px) rotateX(-30deg) scale(0.92);
  }
  to {
    opacity: 1;
    transform: perspective(1000px) translateY(0) rotateX(0) scale(1);
  }
}

/* Webkit scrollbar hiding */
.onboarding-scroll::-webkit-scrollbar {
  display: none;
}

/* Staggered entrance animation */
.step-body > * {
  animation: fadeUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.step-body > :nth-child(1) {
  animation-delay: 0ms;
}
.step-body > :nth-child(2) {
  animation-delay: 50ms;
}
.step-body > :nth-child(3) {
  animation-delay: 75ms;
}
.step-body > :nth-child(4) {
  animation-delay: 120ms;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Scroll-hint arrow bounce */
.scroll-hint-arrow {
  animation: hintBounce 5s cubic-bezier(0.22, 1, 0.36, 1) 3s 3;
}

@keyframes hintBounce {
  0%,
  100% {
    transform: translateY(0);
  }
  6% {
    transform: translateY(4px);
  }
  12% {
    transform: translateY(-1.5px);
  }
  16% {
    transform: translateY(0);
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .step-body > *,
  .scroll-hint-arrow {
    animation: none;
  }
}
</style>
